import React, { useRef } from 'react'
import {
  Box,
  Button,
  TextField,
  Card,
  CardContent,
  Typography,
  IconButton,
  InputAdornment
} from '@mui/material'
import UploadIcon from '@mui/icons-material/CloudUpload'
import AddIcon from '@mui/icons-material/Add'
import ClearIcon from '@mui/icons-material/Clear'
import CloseIcon from '@mui/icons-material/Close'
import { TrashIcon } from '../../icons'
import { fileUpload, fileDelete } from '../../api/uploadApi'

const AddRecruiterCard = ({
  showForm,
  name,
  logo,         // should be URL string, not File or blob
  uploading = false,
  uploadProgress = 0,
  error = '',
  onFieldChange,
  onShowForm,
  onSave,
  onDelete,
  onRemoveLogo,
  onUploadLogo,
}) => {
  const fileInputRef = useRef(null)

  const handleUpload = async (e) => {
    const file = e.target.files[0]
   // Allowed MIME types
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg",];

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      alert("Only image files (jpg, jpeg, png, webp) are allowed.");
      return;
    }
    if (file) {
      onFieldChange('uploading', true)
      onFieldChange('error', '')
      try {
        const data = await fileUpload(file, prog => {
          onFieldChange('uploadProgress', prog)
        })
        onFieldChange('logo', data.url || '')
      } catch {
        onFieldChange('error', 'Logo upload failed!')
      }
      onFieldChange('uploading', false)
      onFieldChange('uploadProgress', 0)
    }
  }

  // For Remove Logo (delete from server, clear logo field)
  const handleRemoveLogo = async () => {
    if (logo) {
      try {
        await fileDelete(logo)
      } catch {
        onFieldChange('error', 'Failed to delete logo from server')
        return
      }
      onFieldChange('logo', '')
    }
  }

  const handleClearName = () => onFieldChange('name', '')
  const handleDelete = () => {
    onDelete && onDelete()
    // Parent should reset all fields and close form
  }

  const handleSave = () => {
    if (logo && name) {
      onSave && onSave()
    }
  }

  return (
    <>
      {!showForm ? (
        <Card
          sx={{
            p: 2,
            textAlign: 'center',
            border: '1px dashed #0463EF',
            height: '100%',
            display: 'flex',
            background: '#F0F6FE',
            flexDirection: 'column',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={() => onShowForm(true)}
        >
          <CardContent>
            <AddIcon fontSize="large" sx={{ fill: '#0463EF' }} />
            <Typography variant="body2" mt={1}>
              Add New
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ p: 0, textAlign: 'center', boxShadow: 'none' }}>
          <CardContent
            sx={{
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                height: 100,
                width: 150,
                minWidth: 150,
                border: '1px dashed #0463EF',
                background: '#F0F6FE',
                borderRadius: 1,
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                textAlign: 'center',
                flexDirection: 'column',
                position: 'relative',
              }}
              onClick={() => !uploading && fileInputRef.current && fileInputRef.current.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                hidden
                accept="image/*"
                onChange={handleUpload}
                disabled={uploading}
              />
              {uploading ? (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <UploadIcon fontSize="large" sx={{ fill: '#0463EF', opacity: 0.6 }} />
                  <Typography fontSize={13} color="#0463EF" mt={1}>
                    Uploading...
                  </Typography>
                  <Box sx={{ width: 80, mt: 1 }}>
                    <Box sx={{ height: 5, background: '#1075FF', width: `${uploadProgress}%`, borderRadius: 2 }} />
                  </Box>
                </Box>
              ) : logo ? (
                <>
                  <Box
                    component="img"
                    src={logo}
                    alt="Uploaded Logo"
                    sx={{ maxHeight: 80, objectFit: 'contain' }}
                  />
                  {!uploading && (
                    <IconButton
                      size="small"
                      onClick={e => {
                        e.stopPropagation()
                        handleRemoveLogo()
                      }}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: '#fff',
                        '&:hover': { backgroundColor: '#f5f5f5' },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </>
              ) : (
                <>
                  <UploadIcon fontSize="large" sx={{ fill: '#0463EF' }} />
                  <Typography variant="body2" mt={1}>
                    Upload Logo
                  </Typography>
                </>
              )}
            </Box>

            <TextField
              placeholder="Enter Company Name"
              size="small"
              value={name}
              onChange={(e) => onFieldChange('name', e.target.value)}
              fullWidth
              sx={{
                mt: 1,
                '& .MuiInputBase-root': {
                  height: 40,
                  fontSize: '12px',
                },
                '& input': {
                  padding: '10px 12px',
                },
                '& .MuiInputBase-input::placeholder': {
                  fontSize: '13px !important',
                  fontStyle: 'italic',
                  color: '#999999',
                }
              }}
              InputProps={{
                endAdornment: name && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearName} size="small">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              disabled={uploading}
            />

            {error && (
              <Typography color="error" fontSize={13} mb={1}>{error}</Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <IconButton
                onClick={handleDelete}
                sx={{
                  backgroundColor: '#f5f5f5',
                  '&:hover': { backgroundColor: '#e0e0e0' },
                  borderRadius: 1,
                }}
              >
                <TrashIcon color="error" />
              </IconButton>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!logo || !name || uploading}
              >
                Save
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </>
  )
}

export default AddRecruiterCard
